import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';

import { orderEsim } from 'flow/actions/esim-actions';
import { fetchExtension } from 'flow/actions/extension-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { getUserCountryCode } from 'flow/selectors/selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    copyToClipboard,
    copyToClipboardIsSupported,
} from 'flow/utils/dom-utils';
import {
    getDownloadableOrInstalledEsim,
    isDownloadableEsim,
    isInstalledEsim,
} from 'flow/utils/esim-utils';
import { generatePath } from 'flow/utils/router-utils';

const SettingsMobileEsim = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const [showSpinner, setShowSpinner] = useState(false);

    const mobileSubscription = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            extensionKey,
            'mobileSubscription',
        ]),
    );

    const userCountryCode = useAppSelector((state) =>
        getUserCountryCode(state, extensionKey),
    );

    useEffect(() => {
        if (!mobileSubscription) {
            dispatch(fetchExtension(extensionKey, 'all'));
        }
    }, [dispatch, extensionKey, mobileSubscription]);

    const handleOrderEsim = async () => {
        setShowSpinner(true);

        new Promise(() => {
            dispatch(orderEsim(extensionKey));
        })
            .then(() => {
                setShowSpinner(false);
            })
            .catch(() => {
                setShowSpinner(false);
                openModal('confirmationDialog', {
                    title: i18next.t('settings.esim.error.title'),
                    description: i18next.t('settings.esim.error.body'),
                });
            });
    };

    if (!mobileSubscription) {
        return <Loading />;
    }

    const renderClickToCopy = (secondary: string) => {
        if (!copyToClipboardIsSupported()) {
            return null;
        }

        return (
            <IconButton
                onClick={() => copyToClipboard(secondary)}
                aria-label={i18next.t('Tooltip.Title.Copy')}
            >
                <Icon iconKey="copy" className="text-gray-600" />
            </IconButton>
        );
    };

    const hasActivePlasticMainSim = !mobileSubscription
        .get('simcards')
        .filter(
            (simcard) =>
                simcard.has('usage') &&
                simcard.get('isMainSimcard') &&
                !simcard.has('esimProfile'),
        )
        .isEmpty();

    const sim = getDownloadableOrInstalledEsim(mobileSubscription);
    let esimProfile = null;

    if (sim) {
        esimProfile = sim.get('esimProfile');
    }

    if (
        esimProfile?.get('activationCode') &&
        (isDownloadableEsim(esimProfile) || isInstalledEsim(esimProfile))
    ) {
        // The eSIM can be installed but not yet activated in the network because we are waiting for a scheduled SIM activation
        return (
            <Page
                header="eSIM"
                onClickBack={() =>
                    history.push(
                        generatePath(routes['settings.mobile'], {
                            extensionKey,
                        }),
                    )
                }
            >
                {hasActivePlasticMainSim && (
                    <SettingSection>
                        {i18next.t('settings.esim.activation.info')}
                    </SettingSection>
                )}

                <SettingSection>
                    {i18next.t('settings.esim.activation')}
                </SettingSection>
                <div className="tex-gray-600 px-2 text-sm">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t('settings.esim.activation.qr'),
                        }}
                    />

                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t(
                                'settings.esim.activation.manually',
                            ),
                        }}
                    />

                    {hasActivePlasticMainSim && (
                        <div>
                            {i18next.t(
                                'settings.esim.activation.info.remove_plastic',
                            )}
                        </div>
                    )}
                </div>

                <SettingSection>{i18next.t('settings.esim.qr')}</SettingSection>
                <div className="p-2">
                    <QRCodeCanvas
                        value={sim.get('esimProfile').get('activationCode')}
                    />
                </div>

                <Divider />

                <SettingRow
                    cta={
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-gray-600">
                                {sim.get('esimProfile').get('smdpAddress')}
                            </div>
                            {renderClickToCopy(
                                sim.get('esimProfile').get('smdpAddress'),
                            )}
                        </div>
                    }
                >
                    {i18next.t('settings.esim.smdp_address')}
                </SettingRow>

                <SettingRow
                    cta={
                        <div className="flex items-center justify-between gap-3 whitespace-nowrap">
                            <div className="text-sm text-gray-600">
                                {sim.get('esimProfile').get('matchingId')}
                            </div>
                            {renderClickToCopy(
                                sim.get('esimProfile').get('matchingId'),
                            )}
                        </div>
                    }
                >
                    {i18next.t('settings.esim.activation_code.iphone')}
                </SettingRow>

                <SettingRow
                    cta={
                        <div className="flex items-center justify-between gap-3 whitespace-nowrap">
                            <div className="text-sm text-gray-600">
                                {sim.get('esimProfile').get('activationCode')}
                            </div>
                            {renderClickToCopy(
                                sim.get('esimProfile').get('activationCode'),
                            )}
                        </div>
                    }
                >
                    {i18next.t('settings.esim.activation_code.android')}
                </SettingRow>

                <SettingRow
                    cta={
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-gray-600">
                                {sim.get('puk1')}
                            </div>
                            {renderClickToCopy(sim.get('puk1'))}
                        </div>
                    }
                >
                    {i18next.t('setting.puk')}
                </SettingRow>

                <SettingRow
                    cta={
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-sm text-gray-600">
                                {sim.get('pin1')}
                            </div>
                            {renderClickToCopy(sim.get('pin1'))}
                        </div>
                    }
                >
                    {i18next.t('settings.voicemail_pin')}
                </SettingRow>
            </Page>
        );
    } else {
        return (
            <Page
                header="eSIM"
                onClickBack={() =>
                    history.push(
                        generatePath(routes['settings.mobile'], {
                            extensionKey,
                        }),
                    )
                }
            >
                <div className="tex-gray-600 mb-3 px-2 text-sm">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t('settings.esim.elevator_pitch'),
                        }}
                    />

                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t('settings.esim.device_support'),
                        }}
                    />
                </div>
                <Button onClick={() => handleOrderEsim()} color="ghost">
                    {showSpinner ? (
                        <Loading size={32} />
                    ) : (
                        i18next.t('settings.esim.order')
                    )}
                </Button>
                {hasActivePlasticMainSim ? (
                    <div className="mt-3 text-gray-600">
                        {i18next.t('settings.esim.activation.info.next_step')}
                    </div>
                ) : null}

                {userCountryCode.toLowerCase() === 'fi' ? (
                    <div className="mt-3 text-gray-600">
                        {i18next.t(
                            'settings.esim.activation.info.mobileid.finland',
                        )}
                    </div>
                ) : null}
            </Page>
        );
    }
};

export default SettingsMobileEsim;
